import { useMemo, useState } from "react";

import { HistoryEntityType } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Button, Loader, Modal, Select } from "@eisox/design-system";
import { ArrowRightIcon, CalendarIcon, CrossIcon, DataIcon, PointChartIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import {
  DEFAULT_POINTS,
  getDateOptions,
  getUnitAxesMapping,
  getValvePointLabel,
  getValvePointOptions,
  MAX_VALVE_HISTORY_SELECTIONS,
  transformHistoryData,
  ValvePoints,
} from "~/helpers/history.helper";
import { useBuildingHistory } from "~/hooks/use-building-history";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";
import type { EnhancedValve } from "~/types/valve.type";

import { DataExportPopup } from "./data-export-popup";
import { ValveHistoryChart } from "./valve-history-chart";

interface ValveHistoryDialogProps extends Modal.RootProps {
  buildingId: string;
  valves: EnhancedValve[];
  valveId?: string;
}

export const ValveHistoryDialog: React.FC<ValveHistoryDialogProps> = ({
  buildingId,
  valves,
  valveId: valveIdProp,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { role } = useBuildingContext("ValveHistoryDialog");

  const valvesWithMac = valves
    .filter((v): v is EnhancedValve & { mac: string } => !!v.mac)
    .sort((a, b) => a.uid - b.uid);

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen,
  });

  const [valveId, setValveId] = useState<string>(valveIdProp ? valveIdProp : (valvesWithMac[0]?.id ?? ""));
  const [startDate, setStartDate] = useState(dayjs().startOf("day").toISOString());
  const [points, setPoints] = useState<string[]>(DEFAULT_POINTS);

  const endDate = dayjs(startDate).endOf("day").toISOString();

  const fieldOptions = getValvePointOptions()
    .filter(f => f.value !== ValvePoints.TRIG_CUR)
    .map(f => ({
      ...f,
      disabled: points.length >= MAX_VALVE_HISTORY_SELECTIONS && !points.includes(f.value),
    }));

  const series = useMemo(() => points.map(point => ({ entityId: valveId, point })), [points, valveId]);

  const { data, isLoading } = useBuildingHistory({
    buildingId,
    startDate,
    endDate,
    entityType: HistoryEntityType.VALVE,
    series,
    enabled: valvesWithMac.length > 0 && points.length > 0 && !!valveId && open,
  });

  const chartData = data ? transformHistoryData(data, points, role) : [];
  const unitAxesMapping = getUnitAxesMapping(points);
  const labels = Object.fromEntries(points.map(point => [point, getValvePointLabel(point)]));
  const dateOptions = getDateOptions();

  if (valvesWithMac.length === 0) return null;

  return (
    <Modal.Root {...props} open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>
      <Modal.Content className="flex h-full w-full flex-col">
        <Modal.Header title={m.history()} subtitle="" icon={<PointChartIcon />}>
          <Modal.Close asChild>
            <CrossIcon className="h-3.75 w-3.75 cursor-pointer" />
          </Modal.Close>
        </Modal.Header>
        <div className="*:min-w-0 mb-5 gap-2.5 flex items-center *:h-full *:flex-1">
          <Select.Root
            multiple
            value={points}
            onValueChange={value => setPoints(value.slice(0, MAX_VALVE_HISTORY_SELECTIONS))}
          >
            <Select.Trigger>
              {isMobile ? <DataIcon style={{ flexShrink: 0 }} /> : <Select.Value placeholder={m.select()} />}
            </Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {fieldOptions.map(f => (
                  <Select.Item key={f.value} value={f.value} disabled={f.disabled}>
                    {f.name}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
          <Select.Root multiple={false} value={startDate} onValueChange={setStartDate}>
            <Select.Trigger>{isMobile ? <CalendarIcon style={{ flexShrink: 0 }} /> : <Select.Value />}</Select.Trigger>
            <Select.Content>
              <Select.Viewport>
                {dateOptions.map(d => (
                  <Select.Item key={d.value} value={d.value}>
                    {d.name}
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
          <Select.Root multiple={false} value={valveId} onValueChange={setValveId}>
            <Select.Trigger className="flex-3">
              <Select.Value />
            </Select.Trigger>
            <Select.Content>
              {valvesWithMac.length > 10 && <Select.Input placeholder={m.number_mac_rooms()} />}
              <Select.Viewport>
                {valvesWithMac.map(v => (
                  <Select.Item
                    key={v.id}
                    value={v.id}
                    className="[&_span]:font-light whitespace-nowrap [&_span]:text-sm [&_span]:text-primary-dark-gray [&_span]:italic"
                  >
                    {v.uid} - {v.mac} <span>{v.roomName}</span>
                    <Select.ItemText>{v.mac}</Select.ItemText>
                  </Select.Item>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Root>
          {!isMobile && (
            <DataExportPopup
              buildingId={buildingId}
              data={points}
              valves={valvesWithMac}
              date={startDate}
              valveId={valveId}
            >
              <Button className="ml-auto">
                {`${m.export()} (.csv, .xlsx)`}
                <ArrowRightIcon />
              </Button>
            </DataExportPopup>
          )}
        </div>
        <div className="min-h-0 flex flex-[1_1_auto] items-center justify-center">
          {isLoading ? (
            <Loader size={24} />
          ) : (
            <ValveHistoryChart
              data={chartData}
              points={points}
              unitAxesMapping={unitAxesMapping}
              labels={labels}
              startDate={startDate}
            />
          )}
        </div>
      </Modal.Content>
    </Modal.Root>
  );
};
